// Section view -- a single Three.js clipping plane. Can be started from a
// picked face (point + normal, reusing the same picker as the measure tool --
// including the cylinder/hole center snap) or from one of the three
// axis-aligned origin planes through the model's bounding-box center. Once
// set, the plane can be offset in parallel along its own normal and flipped to
// cut the other way. Solid cut regions are filled with a hatch via stencil
// caps (see section-caps.ts).
import * as THREE from 'three';
import { dom, formatOffset, log, onLengthUnitChange } from './ui';
import {
  camera,
  disposeMesh,
  getModelBoundingBox,
  getModelRadius,
  onThemeChange,
  pivot,
  raycaster,
  renderer,
  requestRender,
  scene,
  theme
} from './viewer';
import { enrichPick, isMeasureMode, pickFaceOnModel, setMeasureMode } from './measure';
import { clearSectionCaps, updateSectionCaps } from './section-caps';

/** Origin plane through the model center; colors match CAD RGB axes. */
export type OriginPlaneId = 'yz' | 'xz' | 'xy';

export const ORIGIN_PLANES: Record<OriginPlaneId, { normal: THREE.Vector3; color: number; label: string }> = {
  yz: { normal: new THREE.Vector3(1, 0, 0), color: 0xe74c3c, label: 'YZ' }, // X-normal → red
  xz: { normal: new THREE.Vector3(0, 1, 0), color: 0x2ecc71, label: 'XZ' }, // Y-normal → green
  xy: { normal: new THREE.Vector3(0, 0, 1), color: 0x3498db, label: 'XY' }  // Z-normal → blue
};

const sectionPlane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
let sectionActive = false;
let sectionPickMode = false;
let sectionBasePoint: THREE.Vector3 | null = null;
let sectionBaseNormal: THREE.Vector3 | null = null;
let sectionFlipped = false;
let sectionHelper: THREE.Object3D | null = null;
let sectionArrow: THREE.Object3D | null = null;
let sectionArrowCenter = new THREE.Vector3();
let activeOriginPlane: OriginPlaneId | null = null;
let originGuides: THREE.Group | null = null;
let originGuidesWanted = false;
let hoveredOriginPlane: OriginPlaneId | null = null;

export function isSectionActive(): boolean { return sectionActive; }
export function isSectionPickMode(): boolean { return sectionPickMode; }
/** The drag handle, or null while it is hidden (section panel closed). */
export function getSectionArrow(): THREE.Object3D | null { return originGuidesWanted ? sectionArrow : null; }
export function getSectionArrowCenter(): THREE.Vector3 { return sectionArrowCenter; }
export function getSectionPlane(): THREE.Plane { return sectionPlane; }
export function getActiveOriginPlane(): OriginPlaneId | null { return activeOriginPlane; }

function buildSectionArrow(len: number): THREE.Group {
  const group = new THREE.Group();
  const mat = new THREE.MeshBasicMaterial({ color: theme().accent, depthTest: false, toneMapped: false });
  const shaft = new THREE.Mesh(new THREE.CylinderGeometry(len * 0.028, len * 0.028, len * 0.75, 12), mat);
  shaft.position.y = len * 0.375;
  const cone = new THREE.Mesh(new THREE.ConeGeometry(len * 0.11, len * 0.28, 16), mat);
  cone.position.y = len * 0.75 + len * 0.14;
  group.add(shaft, cone);
  group.renderOrder = 999;
  return group;
}

/** Plane quad + arrow are editing aids: only shown while the section panel is open. */
function applySectionHandleVisibility(): void {
  if (sectionHelper) sectionHelper.visible = originGuidesWanted;
  if (sectionArrow) sectionArrow.visible = originGuidesWanted;
}

function updateSectionHelper(): void {
  if (sectionHelper) { disposeMesh(sectionHelper); sectionHelper = null; }
  if (sectionArrow) { disposeMesh(sectionArrow); sectionArrow = null; }
  if (!sectionActive) {
    clearSectionCaps();
    requestRender();
    return;
  }

  const modelRadius = getModelRadius();
  const size = Math.max(modelRadius * 2.5, 1);
  const geo = new THREE.PlaneGeometry(size, size);
  // depthTest off: the helper is a UI overlay, not scene geometry. Leaving it
  // on causes rotation-dependent z-fighting with coplanar cut faces.
  // Subtle plane indicator; solid cut fill comes from stencil hatch caps.
  const mat = new THREE.MeshBasicMaterial({
    toneMapped: false,
    color: theme().accent,
    transparent: true,
    opacity: 0.08,
    side: THREE.DoubleSide,
    depthTest: false,
    depthWrite: false
  });
  const helper = new THREE.Mesh(geo, mat);
  // Center the finite visual quad on the point of the plane nearest the
  // model's pivot -- not the world origin, which a STEP file's own coordinate
  // origin could be far away from.
  const n = sectionPlane.normal;
  const d = n.dot(pivot) + sectionPlane.constant;
  sectionArrowCenter = pivot.clone().addScaledVector(n, -d);
  // Nudge the quad into the kept half-space. Sitting exactly on the clip plane
  // makes the GPU discard helper fragments (holes/stripes that shift with view).
  const clipEpsilon = Math.max(modelRadius * 1e-4, 1e-3);
  helper.position.copy(sectionArrowCenter).addScaledVector(n, clipEpsilon);
  helper.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), n);
  helper.renderOrder = 50;
  scene.add(helper);
  sectionHelper = helper;

  // Draggable handle: pull this along the plane's normal to offset it.
  // Nudge further into the kept half-space than the helper — the shaft base is
  // a solid end-cap that shows clip artifacts clearly when coplanar.
  const arrow = buildSectionArrow(Math.max(modelRadius * 0.4, 0.5));
  const arrowNudge = Math.max(modelRadius * 0.002, clipEpsilon * 4);
  arrow.position.copy(sectionArrowCenter).addScaledVector(n, arrowNudge);
  arrow.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), n);
  scene.add(arrow);
  sectionArrow = arrow;
  applySectionHandleVisibility();

  updateSectionCaps(sectionPlane, sectionArrowCenter);
  requestRender();
}

export function rebuildSectionPlane(): void {
  if (!sectionBaseNormal || !sectionBasePoint) return;
  const n = sectionBaseNormal.clone();
  if (sectionFlipped) n.negate();
  const offset = parseFloat(dom.sectionOffsetSlider.value) || 0;
  // Offset is along the *current* normal (arrow / kept-side), so drag direction
  // stays consistent after flip.
  const p = sectionBasePoint.clone().addScaledVector(n, offset);
  sectionPlane.normal.copy(n);
  sectionPlane.constant = -n.dot(p);
  renderer.clippingPlanes = sectionActive ? [sectionPlane] : [];
  updateSectionHelper();
}

function syncOriginPlaneButtons(): void {
  const map: Record<OriginPlaneId, HTMLButtonElement> = {
    xy: dom.sectionXYBtn,
    xz: dom.sectionXZBtn,
    yz: dom.sectionYZBtn
  };
  (Object.keys(map) as OriginPlaneId[]).forEach(id => {
    map[id].classList.toggle('active', activeOriginPlane === id);
  });
}

function buildOriginGuides(): THREE.Group {
  const group = new THREE.Group();
  const size = Math.max(getModelRadius() * 0.55, 1);
  (Object.keys(ORIGIN_PLANES) as OriginPlaneId[]).forEach(id => {
    const def = ORIGIN_PLANES[id];
    const planeGroup = new THREE.Group();
    planeGroup.userData.originPlaneId = id;

    const fillMat = new THREE.MeshBasicMaterial({
      toneMapped: false,
      color: def.color,
      transparent: true,
      opacity: 0.22,
      side: THREE.DoubleSide,
      depthTest: false,
      depthWrite: false
    });
    const mesh = new THREE.Mesh(new THREE.PlaneGeometry(size, size), fillMat);
    mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), def.normal);
    mesh.renderOrder = 40;
    mesh.userData.originPlaneId = id;
    planeGroup.add(mesh);

    const edgeMat = new THREE.LineBasicMaterial({ color: def.color, depthTest: false, toneMapped: false });
    const edges = new THREE.LineSegments(
      new THREE.EdgesGeometry(new THREE.PlaneGeometry(size, size)),
      edgeMat
    );
    edges.quaternion.copy(mesh.quaternion);
    edges.renderOrder = 41;
    edges.userData.originPlaneId = id;
    planeGroup.add(edges);

    planeGroup.userData.fillMat = fillMat;
    planeGroup.userData.edgeMat = edgeMat;
    group.add(planeGroup);
  });
  group.position.copy(pivot);
  return group;
}

function originIdFromObject(obj: THREE.Object3D): OriginPlaneId | null {
  let cur: THREE.Object3D | null = obj;
  while (cur) {
    const id = cur.userData.originPlaneId as OriginPlaneId | undefined;
    if (id && ORIGIN_PLANES[id]) return id;
    cur = cur.parent;
  }
  return null;
}

function applyOriginPlaneHoverStyles(): void {
  if (!originGuides) return;
  originGuides.children.forEach(child => {
    const id = child.userData.originPlaneId as OriginPlaneId | undefined;
    if (!id) return;
    const active = id === hoveredOriginPlane;
    const fillMat = child.userData.fillMat as THREE.MeshBasicMaterial | undefined;
    const edgeMat = child.userData.edgeMat as THREE.LineBasicMaterial | undefined;
    if (fillMat) fillMat.opacity = active ? 0.55 : 0.22;
    if (edgeMat) {
      edgeMat.color.setHex(ORIGIN_PLANES[id].color);
      if (active) edgeMat.color.offsetHSL(0, 0, 0.25);
    }
    child.scale.setScalar(active ? 1.06 : 1);
  });
  requestRender();
}

/** Highlight an origin-plane guide under the cursor (or clear with null). */
export function setOriginPlaneHover(id: OriginPlaneId | null): void {
  if (hoveredOriginPlane === id) return;
  hoveredOriginPlane = id;
  applyOriginPlaneHoverStyles();
}

function refreshOriginGuides(): void {
  if (originGuides) {
    disposeMesh(originGuides);
    originGuides = null;
  }
  hoveredOriginPlane = null;
  if (!originGuidesWanted || !getModelBoundingBox()) {
    requestRender();
    return;
  }
  // Hide guides while a section is active — the live helper already shows the cut.
  if (sectionActive) {
    requestRender();
    return;
  }
  originGuides = buildOriginGuides();
  scene.add(originGuides);
  requestRender();
}

/**
 * Section panel open/closed: shows the RGB origin-plane triad (before a cut)
 * or the active cut's plane + drag arrow. The cut itself stays either way.
 */
export function setOriginPlaneGuidesVisible(visible: boolean): void {
  originGuidesWanted = visible;
  applySectionHandleVisibility();
  refreshOriginGuides();
}

/** Ray-pick an origin-plane guide under the cursor (YZ / XZ / XY). */
export function pickOriginPlaneGuide(clientX: number, clientY: number): OriginPlaneId | null {
  if (!originGuides) return null;
  const rect = dom.canvas.getBoundingClientRect();
  const ndc = new THREE.Vector2(
    ((clientX - rect.left) / rect.width) * 2 - 1,
    -((clientY - rect.top) / rect.height) * 2 + 1
  );
  raycaster.setFromCamera(ndc, camera);
  const hits = raycaster.intersectObject(originGuides, true);
  for (let i = 0; i < hits.length; i++) {
    const id = originIdFromObject(hits[i].object);
    if (id) return id;
  }
  return null;
}

export function startSection(point: THREE.Vector3, normal: THREE.Vector3, originId: OriginPlaneId | null = null): void {
  sectionBasePoint = point.clone();
  sectionBaseNormal = normal.clone().normalize();
  sectionFlipped = false;
  sectionActive = true;
  activeOriginPlane = originId;
  const range = Math.max(getModelRadius() * 1.5, 1);
  dom.sectionOffsetSlider.min = String(-range);
  dom.sectionOffsetSlider.max = String(range);
  dom.sectionOffsetSlider.step = String(range / 500);
  dom.sectionOffsetSlider.value = '0';
  dom.sectionOffsetValue.textContent = formatOffset(0);
  // Plane + pick buttons stay visible so you can switch without closing first.
  dom.sectionControlsRow.classList.remove('hidden');
  dom.sectionOffBtn.classList.remove('hidden');
  syncOriginPlaneButtons();
  refreshOriginGuides();
  rebuildSectionPlane();
  log(originId ? `Section ${ORIGIN_PLANES[originId].label} active` : 'Section active', true);
}

export function startOriginSection(id: OriginPlaneId): void {
  if (!getModelBoundingBox()) return;
  setSectionPickMode(false);
  startSection(pivot, ORIGIN_PLANES[id].normal.clone(), id);
}

export function stopSection(): void {
  sectionActive = false;
  sectionBasePoint = null;
  sectionBaseNormal = null;
  activeOriginPlane = null;
  renderer.clippingPlanes = [];
  clearSectionCaps();
  syncOriginPlaneButtons();
  updateSectionHelper();
  refreshOriginGuides();
  dom.sectionControlsRow.classList.add('hidden');
  dom.sectionOffBtn.classList.add('hidden');
  setSectionPickMode(false);
}

export function setSectionPickMode(active: boolean): void {
  sectionPickMode = active;
  dom.sectionPickBtn.classList.toggle('primary', active);
  dom.sectionPickBtn.setAttribute('aria-pressed', active ? 'true' : 'false');
  dom.sectionPickBtn.title = active ? 'Now click a face on the model…' : 'Click a face on the model to cut through it';
  if (active) {
    setMeasureMode(false); // only one picking mode active at a time
    dom.canvas.style.cursor = 'crosshair';
  } else if (!isMeasureMode()) {
    dom.canvas.style.cursor = 'grab';
  }
}

export function handleSectionPick(clientX: number, clientY: number): void {
  const rawPick = pickFaceOnModel(clientX, clientY);
  if (!rawPick) return;
  const pick = enrichPick(rawPick); // snaps to hole/fillet axis center when relevant
  setSectionPickMode(false);
  startSection(pick.point, pick.normal, null);
}

export function setSectionOffset(offset: number): void {
  dom.sectionOffsetSlider.value = String(offset);
  dom.sectionOffsetValue.textContent = formatOffset(offset);
  rebuildSectionPlane();
}

export function flipSection(): void {
  sectionFlipped = !sectionFlipped;
  // Offset is measured along the current normal; negate it so the plane stays
  // put while the cut (and arrow) reverse.
  const offset = parseFloat(dom.sectionOffsetSlider.value) || 0;
  if (offset !== 0) {
    dom.sectionOffsetSlider.value = String(-offset);
    dom.sectionOffsetValue.textContent = formatOffset(-offset);
  }
  rebuildSectionPlane();
}

onThemeChange(() => {
  if (sectionActive) updateSectionHelper();
});

onLengthUnitChange(() => {
  dom.sectionOffsetValue.textContent = formatOffset(parseFloat(dom.sectionOffsetSlider.value) || 0);
});
